import { useMediaQuery } from '@chakra-ui/react';
import {
  ArrowLittleRightIcon,
  Box,
  Button,
  Flex,
  Heading,
  Link,
  SimpleGrid,
  Skeleton,
} from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import {
  extractDealProps,
  OfferingCard,
} from '@r-client/republic/feature/offering-list';
import { useAnalytics } from '@r-client/shared/data/analytics';

import { useGetUsOfferingsQuery } from '../../graphql/get-us-offerings-query';
import {
  IGetUsOfferingsQuery,
  IGetUsOfferingsQueryVariables,
} from '../../graphql/types';

import styles from './us-offerings.module.css';

type IOfferingItem = IGetUsOfferingsQuery['offerings']['edges'][number]['node'];
const isOffering = (node: IOfferingItem | undefined) => {
  return node?.__typename === 'Offering';
};

/* eslint-disable @typescript-eslint/no-empty-interface */
export interface IUsOfferingsProps {
  first?: number;
  scopeFilter?: IGetUsOfferingsQueryVariables['scopeFilter'];
  scopeBaseFilter?: IGetUsOfferingsQueryVariables['baseScopeFilters'];
  viewAllButtonHref?: string;
  viewAllButtonText?: string;
  heading?: string;
  headingColor?: string;
}

export const UsOfferings = observer(function UsOfferings({
  first = 3,
  scopeFilter,
  scopeBaseFilter,
  viewAllButtonHref,
  viewAllButtonText,
  heading,
  headingColor,
}: IUsOfferingsProps) {
  const offeringsQuery = useGetUsOfferingsQuery({
    variables: {
      first,
      scopeFilter: scopeFilter,
      baseScopeFilters: scopeBaseFilter,
    },
  });

  const [isTablet] = useMediaQuery(
    `(min-width: 600px) and (max-width: 1170px)`
  );

  const analytics = useAnalytics();

  const showViewMoreButton = !!viewAllButtonHref;

  return (
    <Box data-testid="us-offerings">
      {(heading || showViewMoreButton) && (
        <Flex
          justifyContent="space-between"
          alignItems={{ base: 'flex-start', lg: 'center' }}
          flexDirection={{ base: 'column', lg: 'row' }}
          mb={{ base: '6', sm: '12' }}
          gap="8"
        >
          {heading && (
            <Heading as="h2" m="0" size="title-md" color={headingColor}>
              {heading}
            </Heading>
          )}

          {showViewMoreButton && (
            <Link
              href={viewAllButtonHref}
              textDecoration="none"
              _hover={{ textDecoration: 'none' }}
              onClick={() =>
                analytics.track({
                  name: 'click_view_all',
                })
              }
              variant="republic-default"
              size="republic-default"
              display={{ base: 'none', lg: 'block' }}
              flexShrink={0}
            >
              <Button variant="secondary" textStyle="lg">
                {viewAllButtonText ?? 'Show all'} <ArrowLittleRightIcon />
              </Button>
            </Link>
          )}
        </Flex>
      )}
      {offeringsQuery.isLoading ? (
        <SimpleGrid
          columns={{ base: 1, sm: 3, md: 3 }}
          spacing={{ base: '6', lg: '35px' }}
          mt="6"
          mx="auto"
        >
          {Array.from({ length: first }, (_, i) => {
            return (
              <Skeleton
                isLoaded
                data-testid="skeleton-offering-card"
                key={`${i}_skeleton`}
                height={isTablet ? '308px' : '503px'}
                bgColor="grey.600"
                borderRadius="12px"
                opacity="8%"
              />
            );
          })}
        </SimpleGrid>
      ) : (
        <SimpleGrid
          columns={{ base: 1, sm: 3, md: 3 }}
          spacing={{ base: '6', lg: '35px' }}
          mt="6"
          mx="auto"
        >
          {offeringsQuery.data?.offerings.edges
            ?.slice(0, first)
            ?.map((offering) => {
              const onCardClick = () => {
                isOffering(offering.node) &&
                  analytics.track({
                    name: 'offering_clicked',
                    params: {
                      offering_id: +offering.node.id,
                      offering_slug: offering.node.slug,
                      spotlight: !!offering.node.spotlight,
                      category:
                        offering.node.verticalTag?.replace('v-', '') ?? null,
                      days_remaining:
                        offering.node.timeRemainingValueForDisplay ??
                        '' + offering.node.timeRemainingUnitForDisplay,
                      trending: offering.node.trending,
                      list_id: 'homepage',
                      tags: offering.node.tags?.map((tag) => tag.displayName),
                      platform: 'web',
                      source: 'republic',
                    },
                  });
              };
              return offering ? (
                <OfferingCard
                  className={styles.card}
                  key={offering.node.id}
                  type={isTablet ? 'small' : 'default'}
                  {...extractDealProps(offering.node)}
                  onClick={onCardClick}
                />
              ) : null;
            })}
        </SimpleGrid>
      )}

      {showViewMoreButton && (
        <Link
          href={viewAllButtonHref}
          textDecoration="none"
          _hover={{ textDecoration: 'none' }}
          onClick={() =>
            analytics.track({
              name: 'click_view_all',
            })
          }
          variant="republic-default"
          size="republic-default"
          display={{ base: 'block', lg: 'none' }}
          mt="6"
        >
          <Button variant="secondary" px="10" w="full">
            {viewAllButtonText ?? 'Show all'} <ArrowLittleRightIcon />
          </Button>
        </Link>
      )}
    </Box>
  );
});
